import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import SvgIcon from '../SvgIcon';

/**
 * 아래로 열리는 선택 칸 — 브라우저 기본 <select> 를 대신한다.
 *
 * 왜 새로 만들었나: 맥 크롬의 기본 <select> 는 「지금 고른 항목이 칸 자리에 오도록」 목록을 위로 펼쳐
 * 칸 위의 안내 문구·머리글을 가린다. 기존 부품(DropDown·InputSquareDropDown·RealDropDown 등)은
 * 값이 내부 상태(초기값만 받음)라 controlled value/onChange 계약을 못 지키고, 목록이 부모 안에 absolute 로 그려져
 * 모달 스크롤 영역(overflow) 에 잘리며, 키보드·비활성 항목·읽기 전용이 없다.
 *
 * - 값 계약은 <select> 와 같다: value(문자열) + onChange(새 값). 같은 값을 다시 고르면 부르지 않는다.
 * - 목록은 body 포털 + fixed 위치(모달 overflow 에 안 잘림). 아래 공간이 모자라고 위가 더 넓으면 칸 바로 위에 붙여 뒤집는다.
 * - 바깥 클릭(캡처 단계 — DialogShell 이 mousedown 전파를 막는다)·Esc·바깥 스크롤·창 크기 변경으로 닫힌다.
 * - 키보드: 칸에서 ↑↓/Enter/Space 로 열고, 목록에서 ↑↓ 로 이동(비활성 건너뜀)·Enter 로 고르기·Esc 로 닫기(칸으로 돌아감),
 *   Tab/Shift+Tab 은 닫고 칸 기준 다음·이전 요소로(같은 층 안에서만).
 * - disabled(읽기 전용)이면 열리지 않는다. 비활성 항목은 흐리게 보이고 고를 수 없다. 현재 값엔 체크 표시.
 */
export type SelectMenuOption = { value: string; label: string; disabled?: boolean };

type Props = {
    value: string;
    options: SelectMenuOption[];
    onChange: (value: string) => void;
    /** 값이 목록에 없을 때(예: 아직 안 고름) 칸에 보일 문구 */
    placeholder?: string;
    disabled?: boolean;
    /** 칸 너비 등(Tailwind). 높이·테두리는 기본값을 쓴다 */
    className?: string;
    'aria-label'?: string;
};

const MENU_MAX_HEIGHT = 280;
const GAP = 4;
const VIEWPORT_MARGIN = 8;

const TABBABLE_SELECTOR =
    'a[href], button, input, select, textarea, [tabindex], [contenteditable="true"]';

/**
 * 트리거 칸 기준 다음·이전 탭 이동 대상. 목록은 body 포털에 있어 브라우저 기본 Tab 이 엉뚱한 곳(BODY·모달 뒤 배경)으로 간다.
 * 그래서 트리거가 들어 있는 맨 바깥 층(body 의 직계 자식 — 모달 포털이면 그 모달, 아니면 앱 #root) 안에서만 찾는다.
 * 그 층 끝이면 트리거에 남는다(모달 밖으로 나가지 않게).
 */
/**
 * 탭으로 갈 수 있는 요소인가 — 못 가는 요소를 고르면 focus() 가 무시돼 포커스가 BODY 에 남는다.
 * tabIndex < 0(-1·-2 …), disabled, fieldset[disabled] 안(첫 legend 예외는 보수적으로 무시 — 더 많이 뺄 뿐),
 * hidden·inert·aria-hidden 안, 자신·조상 중 display:none, visibility:hidden(상속값이라 자신만 본다).
 */
const isTabbable = (el: HTMLElement, scope: HTMLElement): boolean => {
    if (el.tabIndex < 0) return false;
    if ((el as HTMLButtonElement).disabled) return false;
    if (el.closest('fieldset[disabled], [hidden], [inert], [aria-hidden="true"]')) return false;
    if (window.getComputedStyle(el).visibility === 'hidden') return false;
    for (let node: HTMLElement | null = el; node && node !== scope.parentElement; node = node.parentElement) {
        if (window.getComputedStyle(node).display === 'none') return false;
    }
    return true;
};

const findTabNeighbor = (trigger: HTMLElement, backward: boolean): HTMLElement | null => {
    let scope: HTMLElement = trigger;
    while (scope.parentElement && scope.parentElement !== document.body) scope = scope.parentElement;
    const tabbables = Array.from(scope.querySelectorAll<HTMLElement>(TABBABLE_SELECTOR)).filter(
        (el) => el === trigger || isTabbable(el, scope),
    );
    const index = tabbables.indexOf(trigger);
    if (index < 0) return null;
    return tabbables[backward ? index - 1 : index + 1] ?? null;
};

type Position = { left: number; width: number; top?: number; bottom?: number; maxHeight: number };

const SelectMenu = ({ value, options, onChange, placeholder = '골라 주세요', disabled = false, className = '', ...rest }: Props) => {
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [position, setPosition] = useState<Position | null>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const listRef = useRef<HTMLUListElement>(null);
    const listId = useId();

    const selectedIndex = options.findIndex((o) => o.value === value);
    const selected = selectedIndex >= 0 ? options[selectedIndex] : undefined;

    const firstEnabledFrom = useCallback(
        (start: number, step: 1 | -1) => {
            for (let i = start; i >= 0 && i < options.length; i += step) {
                if (!options[i].disabled) return i;
            }
            return -1;
        },
        [options],
    );

    const close = useCallback((focusTrigger = true) => {
        setOpen(false);
        setPosition(null);
        if (focusTrigger) triggerRef.current?.focus();
    }, []);

    const openMenu = () => {
        if (disabled) return;
        setActiveIndex(selectedIndex >= 0 && !options[selectedIndex].disabled ? selectedIndex : firstEnabledFrom(0, 1));
        setOpen(true);
    };

    // 칸 위치로 목록 자리를 잡는다 — 기본은 아래, 모자라면 칸 바로 위
    useLayoutEffect(() => {
        if (!open || !triggerRef.current) return;
        const rect = triggerRef.current.getBoundingClientRect();
        const below = window.innerHeight - rect.bottom - GAP - VIEWPORT_MARGIN;
        const above = rect.top - GAP - VIEWPORT_MARGIN;
        const wanted = Math.min(MENU_MAX_HEIGHT, listRef.current?.scrollHeight ?? MENU_MAX_HEIGHT);
        const flip = below < wanted && above > below;
        setPosition(
            flip
                ? { left: rect.left, width: rect.width, bottom: window.innerHeight - rect.top + GAP, maxHeight: Math.min(MENU_MAX_HEIGHT, above) }
                : { left: rect.left, width: rect.width, top: rect.bottom + GAP, maxHeight: Math.min(MENU_MAX_HEIGHT, Math.max(below, 120)) },
        );
    }, [open]);

    // 바깥 클릭·바깥 스크롤·창 크기 변경이면 닫는다
    useEffect(() => {
        if (!open) return;
        const isInside = (target: EventTarget | null) =>
            target instanceof Node && (!!triggerRef.current?.contains(target) || !!listRef.current?.contains(target));
        const onMouseDown = (e: MouseEvent) => {
            if (!isInside(e.target)) close(false);
        };
        const onScroll = (e: Event) => {
            if (!isInside(e.target)) close(false);
        };
        const onResize = () => close(false);
        document.addEventListener('mousedown', onMouseDown, true);
        document.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', onResize);
        return () => {
            document.removeEventListener('mousedown', onMouseDown, true);
            document.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', onResize);
        };
    }, [open, close]);

    // 열리면 목록에 포커스, 움직인 항목이 보이게
    useEffect(() => {
        if (open && position) listRef.current?.focus();
    }, [open, position]);
    useEffect(() => {
        if (!open || activeIndex < 0) return;
        const el = listRef.current?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`);
        el?.scrollIntoView?.({ block: 'nearest' });
    }, [open, activeIndex]);

    const choose = (index: number) => {
        const option = options[index];
        if (!option || option.disabled) return;
        close();
        if (option.value !== value) onChange(option.value);
    };

    const onTriggerKeyDown = (e: React.KeyboardEvent) => {
        if (disabled) return;
        if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
            e.preventDefault();
            openMenu();
        }
    };

    const onListKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            const next = firstEnabledFrom(activeIndex + 1, 1);
            if (next >= 0) setActiveIndex(next);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            const prev = firstEnabledFrom(activeIndex - 1, -1);
            if (prev >= 0) setActiveIndex(prev);
        } else if (e.key === 'Home') {
            e.preventDefault();
            setActiveIndex(firstEnabledFrom(0, 1));
        } else if (e.key === 'End') {
            e.preventDefault();
            setActiveIndex(firstEnabledFrom(options.length - 1, -1));
        } else if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (activeIndex >= 0) choose(activeIndex);
        } else if (e.key === 'Escape') {
            // 바깥 모달까지 닫히지 않게
            e.preventDefault();
            e.stopPropagation();
            close();
        } else if (e.key === 'Tab') {
            // 목록을 닫고 트리거 칸 기준 다음·이전 요소로 옮긴다(목록이 포털이라 기본 Tab 에 맡기면 모달 밖으로 빠진다).
            e.preventDefault();
            const trigger = triggerRef.current;
            close(false);
            if (trigger) {
                const target = findTabNeighbor(trigger, e.shiftKey) ?? trigger;
                target.focus();
                // 판정을 빠져나간 못 가는 요소였으면 focus() 가 무시된다 — BODY 에 두지 않고 칸에 머문다
                if (document.activeElement !== target) trigger.focus();
            }
        }
    };

    return (
        <>
            <button
                ref={triggerRef}
                type="button"
                role="combobox"
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-controls={open ? listId : undefined}
                aria-label={rest['aria-label']}
                disabled={disabled}
                onClick={() => (open ? close() : openMenu())}
                onKeyDown={onTriggerKeyDown}
                className={`h-[43px] flex items-center justify-between gap-[6px] border border-solid rounded-[10px] px-[12px] text-left text-[15px] outline-none ${
                    open ? 'border-primary' : 'border-[#E6E8EA] focus:border-primary'
                } ${disabled ? 'cursor-default bg-[#F9FAFB] text-[#9CA3AF]' : 'cursor-pointer bg-white text-[#2D3642]'} ${className}`}
            >
                <span className={`truncate ${selected ? '' : 'text-[#9CA3AF]'}`}>{selected ? selected.label : placeholder}</span>
                <span aria-hidden="true" className="flex shrink-0">
                <SvgIcon
                    name={open ? 'SvgUpStrokeArrow' : 'SvgDownStrokeArrow'}
                    size={11}
                    fill="transparent"
                    stroke={disabled ? '#C4C9CF' : '#71757C'}
                    strokeWidth={2}
                />
                </span>
            </button>
            {open &&
                createPortal(
                    <ul
                        ref={listRef}
                        id={listId}
                        role="listbox"
                        tabIndex={-1}
                        aria-label={rest['aria-label']}
                        aria-activedescendant={activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined}
                        onKeyDown={onListKeyDown}
                        // 아직 자리를 못 잡았으면 보이지 않게 그려 높이만 잰다
                        style={{
                            position: 'fixed',
                            left: position?.left ?? 0,
                            top: position?.top,
                            bottom: position?.bottom,
                            minWidth: position?.width,
                            maxHeight: position?.maxHeight ?? MENU_MAX_HEIGHT,
                            visibility: position ? 'visible' : 'hidden',
                        }}
                        className="z-[99999] overflow-y-auto rounded-[10px] border border-solid border-[#E6E8EA] bg-white py-[4px] shadow-lg outline-none"
                    >
                        {options.map((option, index) => {
                            const isSelected = option.value === value;
                            const isActive = index === activeIndex;
                            return (
                                <li
                                    key={option.value}
                                    id={`${listId}-${index}`}
                                    data-index={index}
                                    role="option"
                                    aria-selected={isSelected}
                                    aria-disabled={option.disabled || undefined}
                                    onMouseEnter={() => !option.disabled && setActiveIndex(index)}
                                    onClick={() => choose(index)}
                                    className={`flex items-center justify-between gap-[10px] px-[12px] py-[9px] text-[15px] ${
                                        option.disabled
                                            ? 'cursor-default text-[#C4C9CF]'
                                            : `cursor-pointer ${isSelected ? 'text-primary' : 'text-[#2D3642]'}`
                                    } ${isActive && !option.disabled ? 'bg-[#F2F6FF]' : ''}`}
                                >
                                    <span className="whitespace-nowrap">{option.label}</span>
                                    <span aria-hidden="true" className="w-[14px] shrink-0 flex justify-end">
                                        {isSelected && (
                                            <SvgIcon name="SvgCheck" size={12} fill="transparent" stroke="currentColor" strokeWidth={2} />
                                        )}
                                    </span>
                                </li>
                            );
                        })}
                    </ul>,
                    document.body,
                )}
        </>
    );
};

export default SelectMenu;
